import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileText, Clock, CheckCircle2, AlertCircle, Calendar,
    Search, Filter, Plus, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { AIS_MODULE_PRESETS, formatAisModuleLabel, hasAisModuleMetadata } from '../utils/aisModules';
import { formatDueDate, isPastDue, normalizeDueDateForApi, parseDueDate } from '../utils/assignmentDates';
import { parseRubricLines, parseTextList, rubricPointsTotal } from '../utils/rubricForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Assignments() {
    const { user } = useAuth();
    const [assignments, setAssignments] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');

    // Create assignment state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [courses, setCourses] = useState([]);
    const defaultCreateForm = {
        course_id: '',
        title: '',
        description: '',
        due_date: '',
        max_score: 100,
        module_number: '',
        module_title: '',
        ais_cycle: '',
        required_workspace_tabs: [],
        expected_deliverables: [],
        learning_outcomes_text: '',
        rubric_criteria_text: '',
    };
    const [createForm, setCreateForm] = useState(defaultCreateForm);
    const [creating, setCreating] = useState(false);

    const updateCreateField = (field, value) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
    };

    const updateCreateDueDate = (event) => {
        updateCreateField('due_date', event.currentTarget.value);
    };

    // Submission counts (instructor)
    const [submissionCounts, setSubmissionCounts] = useState({});

    const isInstructor = user.role === 'instructor' || user.role === 'admin';

    const fetchData = useCallback(async () => {
        try {
            const [assignmentsRes, submissionsRes] = await Promise.all([
                axios.get(`${API}/assignments`),
                isInstructor ? Promise.resolve({ data: [] }) : axios.get(`${API}/submissions/my`)
            ]);
            setAssignments(assignmentsRes.data);
            setSubmissions(submissionsRes.data);

            if (isInstructor) {
                const coursesRes = await axios.get(`${API}/courses`);
                setCourses(coursesRes.data);

                if (assignmentsRes.data.length > 0) {
                    const counts = {};
                    await Promise.all(
                        assignmentsRes.data.map(async (a) => {
                            try {
                                const res = await axios.get(`${API}/assignments/${a.id}/submissions`);
                                counts[a.id] = res.data.length;
                            } catch { counts[a.id] = 0; }
                        })
                    );
                    setSubmissionCounts(counts);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load assignments');
        } finally {
            setLoading(false);
        }
    }, [isInstructor]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const formDueDate = String(formData.get('due_date') || createForm.due_date || '').trim();
        const formTitle = String(formData.get('title') || createForm.title || '').trim();
        const formDescription = String(formData.get('description') || createForm.description || '').trim();
        const parsedMaxScore = Number.parseInt(String(formData.get('max_score') || createForm.max_score || 100), 10);
        const normalizedDueDate = normalizeDueDateForApi(formDueDate);
        if (!normalizedDueDate) {
            toast.error('Due date is required');
            return;
        }
        let rubricCriteria = [];
        try {
            rubricCriteria = parseRubricLines(createForm.rubric_criteria_text);
        } catch (error) {
            toast.error(error.message);
            return;
        }
        const finalMaxScore = Number.isFinite(parsedMaxScore) ? parsedMaxScore : 100;
        if (rubricPointsTotal(rubricCriteria) > finalMaxScore) {
            toast.error('Rubric points cannot exceed max score');
            return;
        }

        setCreating(true);
        try {
            const payload = {
                ...createForm,
                title: formTitle,
                description: formDescription,
                due_date: normalizedDueDate,
                max_score: finalMaxScore,
                module_number: createForm.module_number ? parseInt(createForm.module_number) : null,
                learning_outcomes: parseTextList(createForm.learning_outcomes_text),
                rubric_criteria: rubricCriteria,
            };
            delete payload.learning_outcomes_text;
            delete payload.rubric_criteria_text;
            const response = await axios.post(`${API}/assignments`, payload);
            setAssignments([response.data, ...assignments]);
            setShowCreateDialog(false);
            setCreateForm(defaultCreateForm);
            toast.success('Assignment created!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create assignment');
        } finally {
            setCreating(false);
        }
    };

    const applyAisModulePreset = (presetId) => {
        if (presetId === 'none') {
            setCreateForm({
                ...createForm,
                module_number: '',
                module_title: '',
                ais_cycle: '',
                required_workspace_tabs: [],
                expected_deliverables: [],
            });
            return;
        }

        const preset = AIS_MODULE_PRESETS.find((item) => item.id === presetId);
        if (!preset) return;

        setCreateForm({
            ...createForm,
            module_number: String(preset.module_number),
            module_title: preset.module_title,
            ais_cycle: preset.ais_cycle,
            required_workspace_tabs: preset.required_workspace_tabs,
            expected_deliverables: preset.expected_deliverables,
        });
    };

    const getSubmissionStatus = (assignmentId) => {
        return submissions.find(s => s.assignment_id === assignmentId);
    };

    const filteredAssignments = assignments.filter(assignment => {
        const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            assignment.course_title.toLowerCase().includes(searchTerm.toLowerCase());
        if (filter === 'all') return matchesSearch;
        const submission = getSubmissionStatus(assignment.id);
        const isOverdue = isPastDue(assignment.due_date);
        if (filter === 'pending') return matchesSearch && !submission && !isOverdue;
        if (filter === 'submitted') return matchesSearch && submission;
        if (filter === 'overdue') return matchesSearch && !submission && isOverdue;
        if (filter === 'graded') return matchesSearch && submission?.grade !== null;
        return matchesSearch;
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-48 skeleton" />
                <div className="space-y-4">
                    {[1, 2, 3].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="assignments-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">Assignments</h1>
                    <p className="text-slate-600 mt-1">
                        {isInstructor ? 'Manage and grade student assignments' : 'View and submit your assignments'}
                    </p>
                </div>
                {isInstructor && (
                    <Button onClick={() => setShowCreateDialog(true)} className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full" data-testid="create-assignment-btn">
                        <Plus className="w-4 h-4 mr-2" /> Create Assignment
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search assignments..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-assignments-input" />
                </div>
                {!isInstructor && (
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-40" data-testid="filter-select">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="submitted">Submitted</SelectItem>
                            <SelectItem value="graded">Graded</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Assignments List */}
            {filteredAssignments.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No assignments found</h3>
                    <p className="text-slate-500">
                        {searchTerm || filter !== 'all' ? 'Try adjusting your filters' : 'Assignments will appear here when created'}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredAssignments.map((assignment, index) => {
                        const submission = getSubmissionStatus(assignment.id);
                        const dueDate = parseDueDate(assignment.due_date);
                        const isOverdue = isPastDue(assignment.due_date);
                        const isDueSoon = Boolean(dueDate) && !isOverdue && (dueDate - new Date()) < 3 * 24 * 60 * 60 * 1000;

                        return (
                            <Link key={assignment.id} to={`/assignments/${assignment.id}`}
                                className={`block bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow animate-fade-in stagger-${(index % 5) + 1}`}
                                data-testid={`assignment-card-${assignment.id}`}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                        submission?.grade !== null && submission?.grade !== undefined ? 'bg-emerald-50'
                                            : submission ? 'bg-blue-50'
                                            : isOverdue ? 'bg-red-50'
                                            : isDueSoon ? 'bg-amber-50' : 'bg-slate-50'
                                    }`}>
                                        {submission?.grade !== null && submission?.grade !== undefined ? (
                                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                                        ) : submission ? (
                                            <Clock className="w-6 h-6 text-blue-500" />
                                        ) : isOverdue ? (
                                            <AlertCircle className="w-6 h-6 text-red-500" />
                                        ) : (
                                            <FileText className={`w-6 h-6 ${isDueSoon ? 'text-amber-500' : 'text-slate-400'}`} />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <h3 className="font-semibold text-slate-900">{assignment.title}</h3>
                                                <p className="text-sm text-slate-500">{assignment.course_title}</p>
                                                {hasAisModuleMetadata(assignment) && (
                                                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 border border-sky-100">
                                                        <BookOpen className="w-3.5 h-3.5" />
                                                        {formatAisModuleLabel(assignment)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-shrink-0">
                                                {isInstructor ? (
                                                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium" data-testid={`submission-count-${assignment.id}`}>
                                                        {submissionCounts[assignment.id] ?? 0} submitted
                                                    </span>
                                                ) : (
                                                    <>
                                                        {submission?.grade !== null && submission?.grade !== undefined ? (
                                                            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">{submission.grade}/{assignment.max_score}</span>
                                                        ) : submission ? (
                                                            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">Submitted</span>
                                                        ) : isOverdue ? (
                                                            <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">Overdue</span>
                                                        ) : (
                                                            <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">Pending</span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-500 mt-2 line-clamp-2">{assignment.description}</p>
                                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Due: {formatDueDate(assignment.due_date)}</span>
                                            <span>Max Score: {assignment.max_score}</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Create Assignment Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Create Assignment</DialogTitle>
                        <DialogDescription>
                            Select a course, due date, and assignment instructions for student submissions.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <Label>Course</Label>
                            <Select value={createForm.course_id} onValueChange={v => setCreateForm({...createForm, course_id: v})}>
                                <SelectTrigger className="mt-1" data-testid="create-course-select"><SelectValue placeholder="Select a course" /></SelectTrigger>
                                <SelectContent>
                                    {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.code} — {c.title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="create-title-input">Title</Label>
                            <Input id="create-title-input" name="title" value={createForm.title} onChange={e => updateCreateField('title', e.target.value)} className="mt-1" required data-testid="create-title-input" />
                        </div>
                        <div>
                            <Label htmlFor="create-desc-input">Description</Label>
                            <Textarea id="create-desc-input" name="description" value={createForm.description} onChange={e => updateCreateField('description', e.target.value)} className="mt-1" rows={3} required data-testid="create-desc-input" />
                        </div>
                        <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-4 space-y-3">
                            <div>
                                <Label>AIS Module Pattern</Label>
                                <Select value={createForm.module_number ? `module-${createForm.module_number}` : 'none'} onValueChange={applyAisModulePreset}>
                                    <SelectTrigger className="mt-1 bg-white" data-testid="create-ais-module-select">
                                        <SelectValue placeholder="Optional AIS module alignment" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No AIS module alignment</SelectItem>
                                        {AIS_MODULE_PRESETS.map((preset) => (
                                            <SelectItem key={preset.id} value={preset.id}>
                                                Module {preset.module_number} - {preset.module_title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {createForm.module_number && (
                                <div className="grid gap-2 text-xs text-slate-600">
                                    <div><span className="font-semibold text-slate-700">Cycle:</span> {createForm.ais_cycle}</div>
                                    <div><span className="font-semibold text-slate-700">Workspace:</span> {createForm.required_workspace_tabs.join(', ')}</div>
                                    <div><span className="font-semibold text-slate-700">Deliverables:</span> {createForm.expected_deliverables.join(', ')}</div>
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4 space-y-3" data-testid="create-rubric-outcomes-panel">
                            <div>
                                <Label htmlFor="create-learning-outcomes-input">Learning Outcomes</Label>
                                <Textarea
                                    id="create-learning-outcomes-input"
                                    value={createForm.learning_outcomes_text}
                                    onChange={e => updateCreateField('learning_outcomes_text', e.target.value)}
                                    placeholder={"One outcome per line, for example:\nPrepare balanced journal entries\nExplain posting impact on the general ledger"}
                                    className="mt-1 bg-white"
                                    rows={3}
                                    data-testid="create-learning-outcomes-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="create-rubric-criteria-input">Rubric Criteria</Label>
                                <Textarea
                                    id="create-rubric-criteria-input"
                                    value={createForm.rubric_criteria_text}
                                    onChange={e => updateCreateField('rubric_criteria_text', e.target.value)}
                                    placeholder={"Use: Criterion | points | optional description\nJournal entry accuracy | 40 | Debits and credits are correct\nEvidence and explanation | 20"}
                                    className="mt-1 bg-white"
                                    rows={4}
                                    data-testid="create-rubric-criteria-input"
                                />
                                <p className="text-xs text-slate-500 mt-1">Keep this simple for AIS students. Total rubric points must not exceed max score.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="create-due-input">Due Date</Label>
                                <Input id="create-due-input" name="due_date" type="date" value={createForm.due_date} onInput={updateCreateDueDate} onChange={updateCreateDueDate} onBlur={updateCreateDueDate} className="mt-1" required data-testid="create-due-input" />
                            </div>
                            <div>
                                <Label htmlFor="create-score-input">Max Score</Label>
                                <Input id="create-score-input" name="max_score" type="number" min="1" value={createForm.max_score} onChange={e => updateCreateField('max_score', parseInt(e.target.value))} className="mt-1" required data-testid="create-score-input" />
                            </div>
                        </div>
                        <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-100">
                            <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={creating || !createForm.course_id} className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" data-testid="confirm-create-btn">
                                {creating ? 'Creating...' : 'Create Assignment'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
